(function () {
  "use strict";

  const STORAGE_KEY = "fishystuff.data.languages.v1";
  const CACHE_VERSION = 1;
  const MAX_STORED_CHARS = 4096;
  const DOCUMENT_FIELDS = Object.freeze(["cacheVersion", "languages"]);
  const BRAND = Symbol.for("fishystuff.data-language-catalog-cache.v1");
  const CONFIG_BRAND = Symbol.for("fishystuff.language-config-contract.v1");

  function isRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || Object.getPrototypeOf(prototype) === null;
  }

  function freezeState(value) {
    const next = { ...value };
    if (next.languages) next.languages = Object.freeze(next.languages.slice());
    return Object.freeze(next);
  }

  function unavailable(operation, reason) {
    return freezeState({ kind: "unavailable", operation, reason });
  }

  function statesEqual(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  const contract = window.__fishystuffLanguageConfigV1;
  if (!contract || contract[CONFIG_BRAND] !== 1) {
    throw new TypeError("Data-language cache requires config contract v1.");
  }

  function decode(raw) {
    if (typeof raw !== "string") {
      return freezeState({
        kind: "invalid",
        reason: "stored-value-must-be-a-string",
      });
    }
    if (raw.length > MAX_STORED_CHARS) {
      return freezeState({
        kind: "invalid",
        reason: "stored-value-exceeds-v1-limit",
      });
    }
    let document;
    try {
      document = JSON.parse(raw);
    } catch (_error) {
      return freezeState({ kind: "invalid", reason: "malformed-json" });
    }
    if (!isRecord(document)) {
      return freezeState({
        kind: "invalid",
        reason: "cache-document-must-be-an-object",
      });
    }
    if (
      Number.isSafeInteger(document.cacheVersion)
      && document.cacheVersion > CACHE_VERSION
    ) {
      return freezeState({
        kind: "unsupportedVersion",
        foundVersion: document.cacheVersion,
      });
    }
    const fields = Object.keys(document).sort();
    if (
      fields.length !== DOCUMENT_FIELDS.length
      || fields.some((field, index) => field !== DOCUMENT_FIELDS[index])
    ) {
      return freezeState({
        kind: "invalid",
        reason: "cache-fields-must-match-v1",
      });
    }
    if (document.cacheVersion !== CACHE_VERSION) {
      return freezeState({
        kind: "invalid",
        reason: "cache-version-must-be-1",
      });
    }
    if (!Array.isArray(document.languages) || document.languages.length === 0) {
      return freezeState({
        kind: "invalid",
        reason: "cache-languages-must-be-a-non-empty-array",
      });
    }
    const normalized = contract.normalizeDataLanguages(document.languages);
    if (
      normalized.length !== document.languages.length
      || normalized.some((code, index) => code !== document.languages[index])
    ) {
      return freezeState({
        kind: "invalid",
        reason: "cache-languages-must-be-unique-canonical-codes",
      });
    }
    return freezeState({ kind: "ready", languages: normalized });
  }

  function encode(languages) {
    const normalized = contract.normalizeDataLanguages(languages);
    if (
      !Array.isArray(languages)
      || normalized.length === 0
      || normalized.length !== languages.length
      || normalized.some((code, index) => code !== languages[index])
    ) {
      throw new TypeError("Data-language cache requires unique canonical codes.");
    }
    return JSON.stringify({
      cacheVersion: CACHE_VERSION,
      languages: normalized,
    });
  }

  let storage = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch (_error) {
    storage = null;
  }
  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return freezeState(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return state();
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) deliver(listener);
    }
    return state();
  }

  function readRaw(operation = "read") {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return { ok: true, raw: storage.getItem(STORAGE_KEY) };
    } catch (_error) {
      return {
        ok: false,
        state: unavailable(operation, "storage-read-failed"),
      };
    }
  }

  function load() {
    const read = readRaw();
    if (!read.ok) return publish(read.state);
    return publish(read.raw === null
      ? { kind: "missing" }
      : decode(read.raw));
  }

  async function replace(languages) {
    const raw = encode(languages);
    try {
      if (
        !storage
        || typeof storage.setItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(STORAGE_KEY, raw);
      if (storage.getItem(STORAGE_KEY) !== raw) {
        return publish(unavailable("write", "storage-write-not-acknowledged"));
      }
    } catch (_error) {
      return publish(unavailable("write", "storage-write-failed"));
    }
    return publish(decode(raw));
  }

  async function reset() {
    const read = readRaw("remove");
    if (!read.ok) return publish(read.state);
    if (read.raw === null) return publish({ kind: "missing" });
    try {
      if (
        !storage
        || typeof storage.removeItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(STORAGE_KEY);
      if (storage.getItem(STORAGE_KEY) !== null) {
        return publish(unavailable("remove", "storage-remove-not-acknowledged"));
      }
    } catch (_error) {
      return publish(unavailable("remove", "storage-remove-failed"));
    }
    return publish({ kind: "missing" });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Data-language cache listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function handleStorage(event) {
    if (
      !disposed
      && event
      && (event.key === STORAGE_KEY || event.key === null)
    ) {
      load();
    }
  }

  function handlePageShow(event) {
    if (!disposed && event?.persisted === true) load();
  }

  window.addEventListener?.("storage", handleStorage);
  window.addEventListener?.("pageshow", handlePageShow);

  function dispose() {
    if (disposed) return;
    disposed = true;
    listeners.clear();
    window.removeEventListener?.("storage", handleStorage);
    window.removeEventListener?.("pageshow", handlePageShow);
  }

  const cache = Object.freeze({
    [BRAND]: CACHE_VERSION,
    dispose,
    load,
    replace,
    reset,
    state,
    subscribe,
  });
  cache.load();
  Object.defineProperty(window, "__fishystuffDataLanguageCatalogCache", {
    configurable: true,
    value: cache,
    writable: false,
  });
})();
